import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Send, BookmarkCheck, Lock, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import VistoVerdeWizard from './VistoVerdeWizard';
import { formatCountdown } from './ClienteQuoteCalculator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eurFmt = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });
const eur = (v) => `€${eurFmt.format(Number(v || 0))}`;

const franchigieLabel = (offer) => {
  if (offer.use_4vantage) return 'Kasko €500 · Furto/Incendio 0%';
  const [kasko, furto] = String(offer.franchigia_scelta || '1000-10').split('-');
  return `Kasko €${Number(kasko).toLocaleString('it-IT')} · Furto/Incendio ${furto}%`;
};

const gommeLabel = (offer) => {
  if (offer.use_4vantage) return 'Treno di gomme incluso';
  const inv = Number(offer.gomme_invernali || 0);
  const est = Number(offer.gomme_estive || 0);
  if (!inv && !est) return 'Non incluse';
  return [inv && `${inv} invernali`, est && `${est} estive`].filter(Boolean).join(' · ');
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 text-right" data-testid={testId}>{value}</span>
  </div>
);

export const ReferenteProposalPanel = ({ offer, onOfferChanged }) => {
  const { getAuthHeader } = useAuth();
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [timerLabel, setTimerLabel] = useState('');
  const hasVV = Boolean(offer.visto_verde);
  const inLavorazione = Boolean(offer.documenti_conferma_at || offer.in_lavorazione);
  const chiusa = offer.esito === 'non_interessato' || offer.status === 'rifiutato_cliente' || offer.is_sold;

  useEffect(() => {
    if (!hasVV || inLavorazione || !offer.visto_verde_at) { setTimerLabel(''); return undefined; }
    setTimerLabel(formatCountdown(offer.visto_verde_at));
    const t = setInterval(() => setTimerLabel(formatCountdown(offer.visto_verde_at)), 60000);
    return () => clearInterval(t);
  }, [hasVV, inLavorazione, offer.visto_verde_at]);

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}${path}`, {}, { headers: getAuthHeader() });
      if (okMsg) toast.success(okMsg);
      if (onOfferChanged) await onOfferChanged();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const toggleVV = async () => {
    const res = await act(`/client/preventivi/${offer.id}/visto-verde`);
    if (res?.visto_verde) {
      toast.success('Offerta bloccata: completa il percorso guidato entro 48 ore');
      setWizardOpen(true);
    } else if (res) {
      toast.success('Blocco rimosso');
    }
  };

  return (
    <div className="mt-4 rounded-xl border-2 border-blue-200 bg-white p-4 space-y-4" data-testid={`referente-panel-${offer.id}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Lock className="w-4 h-4 text-blue-600" /> Condizioni proposte dal tuo referente
        <span className="ml-auto text-[10px] uppercase text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">Non modificabile</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg bg-blue-50 p-4 flex flex-col justify-center">
          <p className="text-[10px] uppercase text-blue-700">Canone mensile</p>
          <p className="text-3xl font-bold text-blue-900" data-testid={`referente-canone-${offer.id}`}>{eur(offer.canone_finale)}</p>
          <p className="text-[11px] text-slate-500">IVA esclusa</p>
        </div>
        <div>
          <Row label="Durata" value={`${offer.durata_mesi} mesi`} testId={`referente-durata-${offer.id}`} />
          <Row label="Km annui" value={`${Number(offer.km_annui || 0).toLocaleString('it-IT')} km`} testId={`referente-km-${offer.id}`} />
          <Row label="Anticipo" value={Number(offer.anticipo_ivato || offer.anticipo || 0) > 0 ? `${eur(offer.anticipo_ivato || offer.anticipo)}${offer.anticipo_ivato ? ' IVA incl.' : ' + IVA'}` : 'Nessun anticipo'} testId={`referente-anticipo-${offer.id}`} />
          <Row label="Franchigie" value={franchigieLabel(offer)} testId={`referente-franchigie-${offer.id}`} />
          <Row label="Auto sostitutiva" value={offer.auto_sostitutiva_categoria ? `Inclusa (categoria ${offer.auto_sostitutiva_categoria})` : 'Non inclusa'} testId={`referente-auto-sostitutiva-${offer.id}`} />
          <Row label="Pneumatici" value={gommeLabel(offer)} testId={`referente-gomme-${offer.id}`} />
        </div>
      </div>

      {!chiusa && (
        <div className="flex flex-wrap items-center gap-2">
          {!hasVV && !inLavorazione && (
            <div className="flex flex-col items-start gap-1">
              <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={toggleVV} data-testid={`referente-btn-visto-verde-${offer.id}`}>
                <CheckCircle2 className="w-4 h-4" /> Blocca offerta e richiedi fattibilità
              </Button>
              <span className="text-[10px] text-slate-500">Confermi l&apos;offerta nei termini proposti • Non vincolante</span>
            </div>
          )}
          {hasVV && !inLavorazione && (
            <>
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold" data-testid={`referente-timer-${offer.id}`}>
                <Clock className="w-3.5 h-3.5" /> {timerLabel || '48h'} rimanenti
              </div>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setWizardOpen(true)} data-testid={`referente-btn-wizard-${offer.id}`}>Riprendi percorso guidato</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={toggleVV} data-testid={`referente-btn-remove-vv-${offer.id}`}>Rimuovi blocco</Button>
              <Button size="sm" className="gap-1 bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={() => act(`/client/preventivi/${offer.id}/conferma-documenti`, 'Documenti confermati. Il consulente prenderà in carico la pratica.')} data-testid={`referente-btn-conferma-doc-${offer.id}`}>
                <Send className="w-4 h-4" /> Ho inviato i documenti
              </Button>
            </>
          )}
          {inLavorazione && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold" data-testid={`referente-in-lavorazione-${offer.id}`}>
              <BookmarkCheck className="w-4 h-4" /> In lavorazione — il consulente ti aggiornerà a breve
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-slate-500">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <p>Importi mensili IVA esclusa. Per variazioni delle condizioni contatta il tuo referente.</p>
      </div>

      <VistoVerdeWizard open={wizardOpen} onClose={() => setWizardOpen(false)} offer={offer} onCompleted={() => onOfferChanged && onOfferChanged()} />
    </div>
  );
};
